import { api } from "./auth";

const u = (p) => `/admin${p}`;

export const adminApi = {
    kpis: () => api.get(u("/kpis")).then((r) => r.data),
    pipeline: () => api.get(u("/pipeline/summary")).then((r) => r.data),
    hotAlerts: (params = {}) =>
        api.get(u("/hot-alerts"), { params }).then((r) => r.data),
    ackAlert: (id) =>
        api.post(u(`/hot-alerts/${id}/acknowledge`)).then((r) => r.data),

    leads: (params) => api.get(u("/leads"), { params }).then((r) => r.data),
    lead: (id) => api.get(u(`/leads/${id}`)).then((r) => r.data),
    patchLead: (id, body) => api.patch(u(`/leads/${id}`), body).then((r) => r.data),
    assignLead: (id, body) =>
        api.post(u(`/leads/${id}/assign`), body).then((r) => r.data),
    distributeUnassigned: () =>
        api.post(u("/leads/distribute-unassigned"), {}).then((r) => r.data),

    // Phase 11 D — soft delete + restore. Uniform pattern across the 5
    // archivable resources (leads / booking / conversations / recruiting /
    // call-logs). Body is optional; reason is stored on the doc as
    // `delete_reason` for the audit trail.
    softDelete: (resource, id, reason) =>
        api.delete(u(`/${resource}/${id}`), { data: { reason } }).then((r) => r.data),
    restore: (resource, id) =>
        api.post(u(`/${resource}/${id}/restore`)).then((r) => r.data),

    // Phase 11 C — notification bell.
    notificationCounts: () =>
        api.get(u("/notifications/unread-counts")).then((r) => r.data),
    markAllNotificationsRead: () =>
        api.post(u("/notifications/mark-read-all")).then((r) => r.data),

    teamList: (params = {}) =>
        api.get(u("/team"), { params }).then((r) => r.data),
    // Phase 15 — assignable owners (agents + admins + master_admins).
    assignableOwners: () =>
        api.get(u("/team/assignable")).then((r) => r.data),

    // Phase 4 — Twilio Dialer
    dialerAccessToken: () =>
        api.get("/dialer/access-token").then((r) => r.data),
    calls: (params = {}) =>
        api.get(u("/calls"), { params }).then((r) => r.data),
    call: (id) =>
        api.get(u(`/calls/${id}`)).then((r) => r.data),
    callRecordingUrl: (id) => {
        const t = (typeof localStorage !== "undefined" && localStorage.getItem("mm_session")) || "";
        const base = `${process.env.REACT_APP_BACKEND_URL}/api${u(`/calls/${id}/recording`)}`;
        return t ? `${base}?t=${encodeURIComponent(t)}` : base;
    },
    hangupCall: (id) =>
        api.post(`/dialer/calls/${id}/hangup`).then((r) => r.data),
    patchCallNotes: (id, notes) =>
        api.patch(u(`/calls/${id}`), { call_notes: notes }).then((r) => r.data),

    // Phase 5 — Call logs + Policies + Catalog
    callLogs: (params = {}) =>
        api.get("/call-logs", { params }).then((r) => r.data),
    callLog: (id) =>
        api.get(`/call-logs/${id}`).then((r) => r.data),
    createCallLog: (body) =>
        api.post("/call-logs", body).then((r) => r.data),
    patchCallLog: (id, body) =>
        api.patch(`/call-logs/${id}`, body).then((r) => r.data),
    deleteCallLog: (id) =>
        api.delete(`/call-logs/${id}`).then((r) => r.data),
    policies: (params = {}) =>
        api.get("/policies", { params }).then((r) => r.data),
    policy: (id) =>
        api.get(`/policies/${id}`).then((r) => r.data),
    carriers: (params = { active: true }) =>
        api.get("/carriers", { params }).then((r) => r.data),
    policyTypes: (params = { active: true }) =>
        api.get("/policy-types", { params }).then((r) => r.data),

    // Admin CRUD on catalog (carriers + policy types)
    listCarriersAll: async () => {
        const [a, i] = await Promise.all([
            api.get("/carriers", { params: { active: true } }).then((r) => r.data),
            api.get("/carriers", { params: { active: false } }).then((r) => r.data),
        ]);
        return [...a, ...i].sort((x, y) => (x.order || 0) - (y.order || 0));
    },
    listPolicyTypesAll: async () => {
        const [a, i] = await Promise.all([
            api.get("/policy-types", { params: { active: true } }).then((r) => r.data),
            api.get("/policy-types", { params: { active: false } }).then((r) => r.data),
        ]);
        return [...a, ...i].sort((x, y) => (x.order || 0) - (y.order || 0));
    },

    // Phase 5.5 — Carrier portals + payment watch
    carrierPortals: () => api.get(u("/carrier-portals")).then((r) => r.data),
    upsertCarrierPortal: (body) =>
        api.post(u("/carrier-portals"), body).then((r) => r.data),
    patchCarrierPortal: (id, body) =>
        api.patch(u(`/carrier-portals/${id}`), body).then((r) => r.data),
    deleteCarrierPortal: (id) =>
        api.delete(u(`/carrier-portals/${id}`)).then((r) => r.data),
    policyPayLink: (policyId) =>
        api.get(`/policies/${policyId}/pay-link`).then((r) => r.data),
    patchPolicyPayment: (policyId, body) =>
        api.patch(`/policies/${policyId}/payment`, body).then((r) => r.data),
    markPolicyPaid: (policyId) =>
        api.post(`/policies/${policyId}/mark-paid`).then((r) => r.data),
    paymentReminders: (params = {}) =>
        api.get("/payment-reminders", { params }).then((r) => r.data),
    paymentAlerts: (params = {}) =>
        api.get("/payment-alerts", { params }).then((r) => r.data),
    acknowledgePaymentAlert: (id) =>
        api.post(`/payment-alerts/${id}/acknowledge`).then((r) => r.data),
    resolvePaymentAlert: (id, body) =>
        api.post(`/payment-alerts/${id}/resolve`, body).then((r) => r.data),
    paymentWatchSummary: () =>
        api.get(u("/payment-watch/summary")).then((r) => r.data),
    forceRunPaymentWatch: (job = "both") =>
        api.post(u(`/payment-watch/run?job=${job}`)).then((r) => r.data),
    createCarrier: (body) => api.post(u("/carriers"), body).then((r) => r.data),
    patchCarrier: (id, body) => api.patch(u(`/carriers/${id}`), body).then((r) => r.data),
    deleteCarrier: (id) => api.delete(u(`/carriers/${id}`)).then((r) => r.data),
    createPolicyType: (body) => api.post(u("/policy-types"), body).then((r) => r.data),
    patchPolicyType: (id, body) => api.patch(u(`/policy-types/${id}`), body).then((r) => r.data),
    deletePolicyType: (id) => api.delete(u(`/policy-types/${id}`)).then((r) => r.data),
    closedDealsRecent: (limit = 10) =>
        api.get(u("/closed-deals/recent"), { params: { limit } }).then((r) => r.data),

    sessionFull: (sid) => api.get(u(`/sessions/${sid}/full`)).then((r) => r.data),

    conversations: (params) =>
        api.get(u("/conversations"), { params }).then((r) => r.data),

    booking: (params) => api.get(u("/booking"), { params }).then((r) => r.data),
    patchBooking: (id, body) => api.patch(u(`/booking/${id}`), body).then((r) => r.data),

    recruiting: (params) => api.get(u("/recruiting"), { params }).then((r) => r.data),
    patchRecruiting: (id, body) =>
        api.patch(u(`/recruiting/${id}`), body).then((r) => r.data),

    // Phase 6A — ID verification (admin)
    sendIdVerification: (appId, body = {}) =>
        api.post(u(`/applicants/${appId}/send-id-verification`), body).then((r) => r.data),
    regenerateIdVerificationLink: (verId, body = {}) =>
        api.post(u(`/id-verifications/${verId}/regenerate-link`), body).then((r) => r.data),
    getApplicantIdVerification: (appId) =>
        api.get(u(`/applicants/${appId}/id-verification`)).then((r) => r.data),
    listIdVerifications: (params = {}) =>
        api.get(u("/id-verifications"), { params }).then((r) => r.data),
    idVerificationFileUrl: (verId, slot) => {
        const t = (typeof localStorage !== "undefined" && localStorage.getItem("mm_session")) || "";
        const base = `${process.env.REACT_APP_BACKEND_URL}/api${u(`/id-verifications/${verId}/file/${slot}`)}`;
        return t ? `${base}?t=${encodeURIComponent(t)}` : base;
    },
    approveIdVerification: (verId) =>
        api.post(u(`/id-verifications/${verId}/approve`)).then((r) => r.data),
    rejectIdVerification: (verId, reason) =>
        api.post(u(`/id-verifications/${verId}/reject`), { reason }).then((r) => r.data),

    // Phase 6 — Commission rates
    listCommissionRates: (params = { active: true }) =>
        api.get(u("/commission-rates"), { params }).then((r) => r.data),
    createCommissionRate: (body) =>
        api.post(u("/commission-rates"), body).then((r) => r.data),
    patchCommissionRate: (id, body) =>
        api.patch(u(`/commission-rates/${id}`), body).then((r) => r.data),
    deleteCommissionRate: (id) =>
        api.delete(u(`/commission-rates/${id}`)).then((r) => r.data),

    // Phase 6 — Scorecards + reports + activity log
    scorecards: (params) =>
        api.get(u("/scorecards/agents"), { params }).then((r) => r.data),
    scorecardForAgent: (agentId, params) =>
        api.get(u(`/scorecards/agents/${agentId}`), { params }).then((r) => r.data),
    reportPoliciesByAgent: (params) =>
        api.get(u("/reports/policies-by-agent"), { params }).then((r) => r.data),
    reportFunnel: (params) =>
        api.get(u("/reports/funnel"), { params }).then((r) => r.data),
    activityLog: (params) =>
        api.get(u("/activity-log"), { params }).then((r) => r.data),

    // Phase 7 + 9 — Invites, users, hierarchy, my dashboard
    listInvites: (params = {}) =>
        api.get(u("/invites"), { params }).then((r) => r.data),
    createInvite: (body) =>
        api.post(u("/invites"), body).then((r) => r.data),
    revokeInvite: (id) =>
        api.post(u(`/invites/${id}/revoke`)).then((r) => r.data),
    resendInvite: (id) =>
        api.post(u(`/invites/${id}/resend`)).then((r) => r.data),
    deleteInvite: (id) =>
        api.delete(u(`/invites/${id}`)).then((r) => r.data),
    listUsers: () =>
        api.get(u("/users")).then((r) => r.data),
    changeUserRole: (userId, role) =>
        api.patch(u(`/users/${userId}/role`), { role }).then((r) => r.data),
    // Phase 9 — free-form upline/downline
    changeUserManager: (userId, managerId) =>
        api.patch(u(`/users/${userId}/manager`), { manager_id: managerId }).then((r) => r.data),
    myDashboard: () =>
        api.get("/my/dashboard").then((r) => r.data),
    myUpline: () =>
        api.get("/my/upline").then((r) => r.data),
    myTeam: (depth = "direct") =>
        api.get("/my/team", { params: { depth } }).then((r) => r.data),

    settings: () => api.get(u("/settings")).then((r) => r.data),
    updateSettings: (body) => api.put(u("/settings"), body).then((r) => r.data),

    voiceModels: () => api.get(u("/voice/models")).then((r) => r.data),
    // Returns a Blob (audio/mpeg) — caller creates an ObjectURL.
    voicePreview: (text) =>
        api
            .get(u("/voice/preview"), {
                params: text ? { text } : {},
                responseType: "blob",
            })
            .then((r) => r.data),

    createTestimonial: (body) => api.post(u("/testimonials"), body).then((r) => r.data),
    updateTestimonial: (id, body) =>
        api.patch(u(`/testimonials/${id}`), body).then((r) => r.data),
    deleteTestimonial: (id) => api.delete(u(`/testimonials/${id}`)).then((r) => r.data),

    createFaq: (body) => api.post(u("/faq"), body).then((r) => r.data),
    updateFaq: (id, body) => api.patch(u(`/faq/${id}`), body).then((r) => r.data),
    deleteFaq: (id) => api.delete(u(`/faq/${id}`)).then((r) => r.data),

    search: (q) => api.get(u("/search"), { params: { q } }).then((r) => r.data),
};

export const PIPELINE_STAGES = [
    "New",
    "Jarvis Qualified",
    "Needs Review",
    "Approved",
    "Contact Ready",
    "Scheduled",
    "Sent to Brent",
    "Closed",
];

export const ADVISOR_VISIBLE_STAGES = new Set([
    "Approved",
    "Contact Ready",
    "Scheduled",
    "Sent to Brent",
    "Closed",
]);

export const STAGE_COLORS = {
    New: "bg-slate-100 text-slate-800 border-slate-200",
    "Jarvis Qualified": "bg-sky-100 text-sky-900 border-sky-200",
    "Needs Review": "bg-amber-100 text-amber-900 border-amber-200",
    Approved: "bg-emerald-100 text-emerald-900 border-emerald-200",
    "Contact Ready": "bg-teal-100 text-teal-900 border-teal-200",
    Scheduled: "bg-indigo-100 text-indigo-900 border-indigo-200",
    "Sent to Brent": "bg-violet-100 text-violet-900 border-violet-200",
    Closed: "bg-zinc-100 text-zinc-700 border-zinc-200",
};
